import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { format, isValid, parse, parseISO } from "date-fns";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import RequestStatusTimeline from "components/DesignSystem/RequestStatusTimeline";
import ApprovalEditAuditTrail from "components/DesignSystem/ApprovalEditAuditTrail";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import {
  detailAccentPalette,
  detailCardSx,
  detailSectionLabelSx,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import {
  designColors,
  designShell,
} from "services/constants/designTokens";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { approvePanelUi, useGoToApprovalCenter } from "../approveUi";
import { canonicalCompOffStatus, compOffStatusVisual } from "./CompOffStatusCards";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { Apply as CompOffApplyPanel } from "./Apply";

// Same date-format tolerance WorkAdjustment/index.js's own toISODate uses —
// the actual-special-attendance API's date fields aren't guaranteed to
// already be ISO, so this normalizes DD-MM-YYYY-ish strings before resend.
const toISODate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split("-");
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return dateStr;
};

const detailAccent = {
  earned: detailAccentPalette.indigo,
  requested: detailAccentPalette.blue,
  reason: detailAccentPalette.orange,
  rejectReason: detailAccentPalette.purple,
};

const parseKnownDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;

  const text = String(value).trim();
  const knownFormats = ["dd-MM-yyyy", "yyyy-MM-dd", "dd/MM/yyyy", "yyyy/MM/dd"];

  for (const pattern of knownFormats) {
    const parsed = parse(text, pattern, new Date());
    if (isValid(parsed)) return parsed;
  }

  const parsedIso = parseISO(text);
  return isValid(parsedIso) ? parsedIso : null;
};

const formatDisplayDate = (value) => {
  const parsed = parseKnownDate(value);
  return parsed ? format(parsed, "dd MMM yyyy") : value || "-";
};

const formatDisplayDateTime = (value) => {
  const parsed = parseKnownDate(value);
  if (!parsed) return value || "-";
  const hasTime = /\d{1,2}:\d{2}/.test(String(value));
  return format(parsed, hasTime ? "dd MMM yyyy, hh:mm a" : "dd MMM yyyy");
};

export const CompOffDetailPanel = ({
  open,
  onClose,
  compOff,
  checkAccess,
  setReCallApi,
  expanded = false,
  onToggleExpand,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const goToApprovalCenter = useGoToApprovalCenter();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  // No dedicated cancel-an-approved-comp-off endpoint exists yet (unlike
  // Leave's cancel_create_leave_request/ or Permission's permission_cancel/)
  // — this reuses the same approve/reject endpoint below with
  // status: "Cancelled" as a first attempt. Needs verifying against the
  // real backend; if it's rejected/ignored there, this needs a real
  // dedicated endpoint the same way Leave and Permission have one.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const { domain, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  // See Leave/LeaveDetailPanel.js — Approve/Reject/Cancel used to be gated
  // by plain editor permission (checkAccess), letting any editor approve,
  // reject or cancel someone else's comp off with no approver permission
  // at all. Now requires real approver permission (either level counts).
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Comp Off",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Comp Off",
    );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: { rejected_reason: "" },
  });

  useEffect(() => {
    setRejectDialogOpen(false);
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
    setEditPanelOpen(false);
    reset({ rejected_reason: "" });
  }, [compOff, reset]);

  useEffect(() => {
    register("rejected_reason", {
      required: "Reject reason is required",
      validate: {
        minLengthNoSpaces: (value) => {
          if (value?.trim().length < 3) return minimum3Characters;
          if (value?.trim().length > 255) return "Maximum length is 255 characters";
          return true;
        },
        atleastOneCharacterWithSpaceReg: (value) =>
          atleastOneCharacterWithSpace.test(value) || "Atleast one alphabet required",
      },
    });
  }, [register]);

  const statusFlowData = useMemo(
    () => ({
      ...(compOff || {}),
      applied_by:
        compOff?.applied_by ||
        compOff?.employee_name ||
        compOff?.username ||
        compOff?.requested_by,
      applied_on: formatDisplayDateTime(
        compOff?.applied_on ||
          compOff?.applied_date ||
          compOff?.created_date ||
          compOff?.created_at ||
          compOff?.requested_date,
      ),
      // compOff?.approver_name is the assigned approver, stamped at apply
      // time before anyone has acted -- it is not proof of approval and
      // must not be preferred over (or substitute for) the backend's
      // status-derived approved_by.
      approved_by: compOff?.approved_by,
      approved_at: formatDisplayDateTime(
        compOff?.approved_on || compOff?.approved_at,
      ),
      rejected_by: compOff?.rejected_by,
      rejected_at: formatDisplayDateTime(
        compOff?.rejected_on || compOff?.rejected_at,
      ),
    }),
    [compOff],
  );

  if (!compOff) return null;

  const status = canonicalCompOffStatus(compOff?.status);
  const isPending = status === "Pending";
  // Requested per user: allow cancelling a comp off request even after it
  // has been Approved (not just while Pending, unlike every other request
  // type's own Cancel button — see canCancel across MyZoneApply's screens).
  const canCancelApproved = status === "Approved";
  const employeeName = compOff?.employee_name || "N/A";
  const employeeId = compOff?.employee_id || compOff?.emp_id || "-";
  const department = compOff?.department_name || compOff?.department || "-";
  const designation = compOff?.designation_name || compOff?.designation || "-";
  const requestReason = compOff?.reason || compOff?.description || "-";
  const rejectedReason = compOff?.rejected_reason || "";
  const decisionReasonLabel =
    status === "Cancelled" ? "Cancel Reason" : "Reject Reason";

  const handleCloseAction = () => {
    onClose?.();
  };

  const handleEditDone = () => {
    setEditPanelOpen(false);
    setReCallApi?.(true);
    onClose?.();
  };

  const updateCompOffStatus = async (requestStatus, reason) => {
    const payload = {
      id: compOff.id,
      user_id: compOff.user_id,
      attendance_type: "Comp off",
      reason: compOff?.reason,
      status: requestStatus,
      earned_date: toISODate(compOff?.earned_date),
      requested_date: toISODate(compOff?.requested_date),
      ...(requestStatus === "Rejected" && { rejected_reason: reason }),
      // Field name is a guess (mirrors rejected_reason above) — there's no
      // existing precedent for a comp off cancel payload to copy, since
      // this status transition has never been sent to this endpoint
      // before. Adjust once the actual backend contract is confirmed.
      ...(requestStatus === "Cancelled" && { cancelled_reason: reason }),
    };
    const response = await customAPI(
      `${domain}team_zone/approve/work_adjustment/actual-special-attendance/?company_id=${requestCompanyId}`,
      "PUT",
      payload,
    );
    if (response?.ok) {
      showToast(
        requestStatus === "Rejected" ? "error" : "success",
        response?.responseDetails?.success?.[0]?.message ||
          `Comp off request ${requestStatus.toLowerCase()}.`,
      );
      setReCallApi?.(true);
      setRejectDialogOpen(false);
      setCancelDialogOpen(false);
      onClose?.();
      return true;
    }
    showToast(
      "error",
      response?.message ||
        `Couldn't ${
          requestStatus === "Approved"
            ? "approve"
            : requestStatus === "Cancelled"
              ? "cancel"
              : "reject"
        } the comp off request. Please try again.`,
    );
    return false;
  };

  const handleApprove = async () => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to approve this comp off request",
      );
      return;
    }
    setApproving(true);
    await updateCompOffStatus("Approved");
    setApproving(false);
  };

  const submitReject = async (data) => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to reject this comp off request",
      );
      return;
    }
    setRejecting(true);
    await updateCompOffStatus("Rejected", data?.rejected_reason);
    setRejecting(false);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    reset({ rejected_reason: "" });
    clearErrors("rejected_reason");
  };

  const submitCancel = async () => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setCancelReasonError("Reason for cancellation is required");
      return;
    }
    if (trimmed.length < 3) {
      setCancelReasonError(minimum3Characters);
      return;
    }
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to cancel this comp off request",
      );
      return;
    }
    setCancelling(true);
    await updateCompOffStatus("Cancelled", trimmed);
    setCancelling(false);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
  };

  // Reject/Approve/Cancel below used to always render, only disabled (via
  // AccessGatedAction) when hasApproverAccess was false — leaving a
  // bordered footer strip with unusable buttons for an editor-only viewer.
  // Each branch now resolves to real content or a genuine `null` (same fix
  // as OverTime/DetailPanel.js) so GlobalSidePanel's own footer border only
  // draws when there's actually something to show.
  const footerContent = isPending ? (
    hasApproverAccess ? (
      <Mui.Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 1.5,
        }}
      >
        <Mui.Button
          variant="outlined"
          startIcon={<HighlightOffIcon />}
          onClick={() => setRejectDialogOpen(true)}
          sx={{
            ...designShell.panelDangerButton,
            color: "#B42318",
            borderColor: "#F5C2C7",
            backgroundColor: "#FEF3F2",
            "&:hover": {
              borderColor: "#E59CA5",
              backgroundColor: "#FEE4E2",
            },
          }}
        >
          Reject
        </Mui.Button>
        <PrimaryButton
          startIcon={<CheckCircleOutlineIcon />}
          loading={approving}
          onClick={handleApprove}
        >
          Approve
        </PrimaryButton>
      </Mui.Box>
    ) : null
  ) : canCancelApproved ? (
    hasApproverAccess ? (
      <Mui.Stack spacing={1.5}>
        <Mui.Typography
          sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
        >
          This request has already been approved.
        </Mui.Typography>
        <RequestCancelButton onClick={() => setCancelDialogOpen(true)}>
          Cancel Comp Off
        </RequestCancelButton>
      </Mui.Stack>
    ) : null
  ) : (
    <Mui.Typography
      sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
    >
      This request has already been {status?.toLowerCase() || "processed"}.
    </Mui.Typography>
  );

  const rejectDialog = (
    <GlobalConfirmDialog
      open={rejectDialogOpen}
      title="Reject Comp Off Request"
      onCancel={closeRejectDialog}
      onConfirm={() => {
        if (!watch("rejected_reason")?.trim()) {
          setError("rejected_reason", { type: "required", message: "Reject reason is required" });
          return;
        }
        handleSubmit(submitReject)();
      }}
      confirmLabel="Reject"
      cancelLabel="Cancel"
      loading={rejecting}
      danger
      reasonValue={watch("rejected_reason") || ""}
      onReasonChange={(value) => setValue("rejected_reason", value, { shouldValidate: true })}
      reasonError={errors?.rejected_reason?.message || ""}
      reasonPlaceholder="Provide a reason for rejection"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        Enter the reason for rejecting this Comp Off request.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  const cancelDialog = (
    <GlobalConfirmDialog
      open={cancelDialogOpen}
      title="Cancel Comp Off Request"
      onCancel={closeCancelDialog}
      onConfirm={submitCancel}
      confirmLabel="Continue"
      cancelLabel="Keep"
      loading={cancelling}
      loadingLabel="Cancelling..."
      danger
      reasonValue={cancelReason}
      onReasonChange={(value) => {
        setCancelReason(value);
        if (cancelReasonError) setCancelReasonError("");
      }}
      reasonError={cancelReasonError}
      reasonPlaceholder="Provide a reason for cancelling this approved comp off"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        This comp off request has already been approved. Enter a reason to
        cancel it.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={handleCloseAction}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        title="Comp Off Request"
        subtitle="Request details and status"
        headerActions={
          isPending && checkAccess ? (
            <Mui.Tooltip title="Edit comp off request" arrow>
              <Mui.IconButton
                aria-label="Edit comp off request"
                size="small"
                onClick={() => setEditPanelOpen(true)}
                sx={{
                  ...designShell.panelSoftIconButton,
                  color: designColors.slate500,
                }}
              >
                <EditIcon fontSize="small" />
              </Mui.IconButton>
            </Mui.Tooltip>
          ) : null
        }
        headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
        PaperProps={{
          sx: {
            backgroundColor: designColors.white,
            maxWidth: "92vw",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
          },
        }}
        breadcrumb={[
          { label: "Approval Center", onClick: goToApprovalCenter },
          { label: "Comp Off", onClick: handleCloseAction },
          employeeName || "Comp Off Request",
        ]}
        footer={footerContent}
      >
        <Mui.Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <RequestDetailHeader
            userDetails={{
              image_url: compOff?.image_url,
              employee_id: employeeId,
              user_name: employeeName,
              department_name: department,
              designation,
            }}
            status={status}
            colorCode={compOff?.color_code || compOffStatusVisual(status).accent}
          />

          <Mui.Box sx={detailCardSx}>
            <Mui.Typography sx={{ ...detailSectionLabelSx, mb: 1.75 }}>
              Request Details
            </Mui.Typography>
            <RequestDetailField
              icon={CalendarMonthIcon}
              label="Comp Off Earned On"
              value={formatDisplayDate(compOff?.earned_date)}
              accent={detailAccent.earned}
            />
            <RequestDetailField
              icon={EventAvailableOutlinedIcon}
              label="Comp Off Requested For"
              value={formatDisplayDate(compOff?.requested_date)}
              accent={detailAccent.requested}
            />
            <RequestDetailField
              icon={DescriptionOutlinedIcon}
              label="Reason"
              value={requestReason}
              accent={detailAccent.reason}
              multiline
              isLast={!rejectedReason}
            />
            {rejectedReason ? (
              <RequestDetailField
                icon={DescriptionOutlinedIcon}
                label={decisionReasonLabel}
                value={rejectedReason}
                accent={detailAccent.rejectReason}
                multiline
                isLast
              />
            ) : null}
          </Mui.Box>

          <RequestStatusTimeline
            endDate={compOff?.requested_date}
            data={statusFlowData}
            variant="stepper"
            hidePendingStep
          />
          <ApprovalEditAuditTrail data={compOff} />
        </Mui.Box>
      </GlobalSidePanel>
      {editPanelOpen ? (
        <CompOffApplyPanel
          open={editPanelOpen}
          onClose={() => setEditPanelOpen(false)}
          setReCallApi={handleEditDone}
          editRecord={compOff}
        />
      ) : null}
      {rejectDialog}
      {cancelDialog}
    </>
  );
};
